import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import { generateFsAdCopy, generateSalesSheetCopy, SalesSheetCopyResult } from '../aiImageStudioApi';
import { AiImageReferenceInput, AiImageStudioConfig, AiImageStudioDraft, DamSearchAssetResult } from '../types';
import { getAiImageAssetUrl } from '../utils';
import { ICategory } from '../../../types/damTypes';
import { IMAGE_QUALITY_HELP, IMAGE_SIZE_HELP, OPENAI_FLARE_MODEL_ID, formatOpenAiQualityLabel } from '../modelCatalog';
import { estimateAiImageCostText } from '../costEstimate';
import FieldHelpIcon from './FieldHelpIcon';

type SalesSheetTemplate = {
  id: string;
  name: string;
  productCount: number;
  assetUrl: string;
  description?: string;
  layoutMode?: 'fixed' | 'free';
  productCountOptions?: number[];
};

type ProductSlot = {
  id: string;
  query: string;
  searching: boolean;
  results: DamSearchAssetResult[];
  selectedAsset: DamSearchAssetResult | null;
  noPrice: boolean;
  price: string;
};

type BannerState = {
  query: string;
  searching: boolean;
  results: DamSearchAssetResult[];
  selectedAsset: DamSearchAssetResult | null;
};

type FsAdTemplate = {
  id: string;
  name: string;
  assetUrl: string;
  aspectRatio: string;
  description: string;
};

interface SalesSheetPanelProps {
  config: AiImageStudioConfig | null;
  categories: ICategory[];
  generating: boolean;
  onSearchDamAssets: (query: string, categoryId?: string) => Promise<DamSearchAssetResult[]>;
  onGenerateDraft: (draft: AiImageStudioDraft) => void;
}

const SALES_SHEET_TEMPLATES: SalesSheetTemplate[] = [
  {
    id: 'sales-sheet-2-products',
    name: 'Mall 2 produkter v1',
    productCount: 2,
    assetUrl: '/sales-sheet-templates/mall-2-produkter.png',
  },
  {
    id: 'sales-sheet-3-products',
    name: 'Mall 3 produkter',
    productCount: 3,
    assetUrl: '/sales-sheet-templates/mall-3-produkter.png',
  },
  {
    id: 'sales-sheet-2-products-v2',
    name: 'Mall 2 produkter v2',
    productCount: 2,
    assetUrl: '/sales-sheet-templates/mall-2-produkter-v2.png',
  },
  {
    id: 'sales-sheet-free-profile',
    name: 'Fri grafisk profil',
    productCount: 2,
    productCountOptions: [1, 2, 3, 4, 5],
    assetUrl: '/sales-sheet-templates/mall-2-produkter-v2.png',
    layoutMode: 'free',
    description: 'Använder Fontana-profilen som stilreferens men låter AI anpassa layouten efter produkter och copy.',
  },
];

const FS_AD_TEMPLATES: FsAdTemplate[] = [
  {
    id: 'fs-grosshandlarn-118x124',
    name: 'Grosshandlarn 118x124',
    assetUrl: '/fs-ad-templates/annons-grosshandlarn-118x124.png',
    aspectRatio: '1:1',
    description: 'Kvadratisk/nära stående annons med annonsbild upptill och produkter i blå profil.',
  },
  {
    id: 'fs-smak-inspiration-190x172',
    name: 'Smak/Inspiration 190x172',
    assetUrl: '/fs-ad-templates/annons-smak-inspiration-190x172.png',
    aspectRatio: '1:1',
    description: 'Bred annons med blå produktdel till vänster och annonsbild till höger.',
  },
  {
    id: 'fs-sommargaster-205x73',
    name: 'Sommargäster 205x73',
    assetUrl: '/fs-ad-templates/annons-sommargaster-205x73.png',
    aspectRatio: '21:9',
    description: 'Smal liggande annons för 1-3 produkter.',
  },
  {
    id: 'fs-smak-inspiration-204x130',
    name: 'Smak/Inspiration 204x130',
    assetUrl: '/fs-ad-templates/annons-smak-inspiration-204x130.png',
    aspectRatio: '3:2',
    description: 'Liggande annons med tydlig blå produktdel och stor annonsbild.',
  },
];

function makeSlots(count: number, previous: ProductSlot[] = []): ProductSlot[] {
  return Array.from({ length: count }, (_, index) => previous[index] || {
    id: `product-${index + 1}`,
    query: '',
    searching: false,
    results: [],
    selectedAsset: null,
    noPrice: false,
    price: '',
  });
}

function referenceId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function assetImageUrl(asset: DamSearchAssetResult | null): string | undefined {
  if (!asset) return undefined;
  return getAiImageAssetUrl(asset.thumbnailUrl || asset.previewUrl || '');
}

function normalizeCategoryName(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function categoryMatches(categoryName: string, targetName: string): boolean {
  const normalizedCategory = normalizeCategoryName(categoryName);
  const normalizedTarget = normalizeCategoryName(targetName);
  return normalizedCategory === normalizedTarget
    || normalizedCategory.includes(normalizedTarget)
    || normalizedTarget.includes(normalizedCategory);
}

function getCategoryAndChildIds(categories: ICategory[], targetNames: string[]): string[] {
  const matchedIds = new Set(
    categories
      .filter((category) => targetNames.some((targetName) => categoryMatches(category.name, targetName)))
      .map((category) => category._id)
  );
  let changed = true;
  while (changed) {
    changed = false;
    for (const category of categories) {
      const parentId = typeof category.parentCategory === 'string'
        ? category.parentCategory
        : category.parentCategory?._id;
      if (parentId && matchedIds.has(parentId) && !matchedIds.has(category._id)) {
        matchedIds.add(category._id);
        changed = true;
      }
    }
  }
  return Array.from(matchedIds);
}

function uniqueAssets(assets: DamSearchAssetResult[]): DamSearchAssetResult[] {
  const seen = new Set<string>();
  return assets.filter((asset) => {
    if (seen.has(asset._id)) return false;
    seen.add(asset._id);
    return true;
  });
}

async function urlToDataUrl(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Kunde inte läsa säljbladsmallen.');
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Kunde inte läsa säljbladsmallen.'));
    reader.readAsDataURL(blob);
  });
}

function estimateReferences(count: number): AiImageReferenceInput[] {
  return Array.from({ length: Math.max(0, count) }, (_, index) => ({
    referenceId: `cost-estimate-${index}`,
    type: 'upload',
  }));
}

function getOpenAiModel(config: AiImageStudioConfig | null): { id: string; provider: string } {
  const openAiModel = config?.models.find((model) => model.provider === 'openai');
  return {
    id: openAiModel?.id || OPENAI_FLARE_MODEL_ID,
    provider: openAiModel?.provider || 'openai',
  };
}

function buildCopyPayloadProduct(slot: ProductSlot) {
  const asset = slot.selectedAsset;
  return {
    productName: asset?.productName || asset?.title || '',
    articleNumber: asset?.articleNumber || '',
    ean: asset?.ean || '',
    netContent: asset?.netContent || '',
    marketingMessage: asset?.marketingMessage || '',
    price: slot.noPrice ? 'Inget pris' : slot.price,
  };
}

function buildSalesSheetPrompt(params: {
  template: SalesSheetTemplate;
  products: ProductSlot[];
  banner: DamSearchAssetResult | null;
  speechBubble: string;
  retailerLine: string;
  copy: SalesSheetCopyResult;
}): string {
  const productLines = params.products.map((slot, index) => {
    const product = buildCopyPayloadProduct(slot);
    return [
      `Produkt ${index + 1}:`,
      `Namn: ${product.productName}`,
      `Artikelnummer: ${product.articleNumber}`,
      `EAN: ${product.ean}`,
      `Nettovikt: ${product.netContent}`,
      `Pris: ${product.price || 'Inget pris'}`,
      `Produktbeskrivning: ${product.marketingMessage}`,
    ].join('\n');
  }).join('\n\n');
  const isFreeLayout = params.template.layoutMode === 'free';
  const layoutInstruction = isFreeLayout
    ? `Använd referensbild 1 som grafisk profil och stilreferens, inte som en exakt låst layout.
Du får skapa en ny professionell säljbladslayout som passar antal produkter (${params.products.length}), produkternas form, bannerbilden och copylängden.
Behåll tydligt Fontana-känslan: mörkblå bottenvåg, Fontana-logotyp längst ned, blå/magenta accentfärger, tydlig rubrikyta, banner/kampanjbild upptill, generös vit/ljus yta för produkt- och säljtext.
Layouten ska fortfarande kännas som ett Fontana-säljblad från samma grafiska familj, men du får variera placering, produktgruppering, pratbubbla, USP-lista och rubrikkomposition om det ger ett starkare säljblad.`
    : `Använd referensbild 1 som layoutmall och följ den mycket noggrant: placeringar, marginaler, produktplatser, bannerområde, pratbubbla, rubrikyta, textytor, typografisk känsla och färgbalans.`;

  return `Skapa ett färdigt svenskt B2B-säljblad som EN exakt renderad bild.

${layoutInstruction}
Referensbild 2 är bannerbilden. Placera den i mallens bannerområde.
Referensbild 3 och framåt är produktbilderna i samma ordning som produktlistan nedan.

VIKTIGT:
- All ny text på bladet ska vara exakt enligt copy och produktdata nedan, med korrekt svensk grammatik, stavning och typografi.
- Hitta inte på artikelnummer, EAN, nettovikt, pris eller produktnamn.
- Om en produkt är markerad "Inget pris" ska ingen prisetikett visas för den produkten.
- Bladet ska se ut som en professionell PDF från InDesign, men output ska vara en bild.
- Inga extra texter, inga vattenmärken, inga AI-kommentarer.

PRODUKTFÖRPACKNINGAR ÄR ORÖRBARA ASSETS:
- Product package references must be pixel-identical in appearance. Do NOT redesign, restyle, retranslate, correct, simplify, redraw, or "improve" them.
- Treat every product package as an untouchable asset: copy it into the layout, do not regenerate it.
- Preserve every visible brand element exactly, including wordmarks, subtexts, stamps, PDO seals, barcodes, weights, nutrition marks and Swedish body text.
- Preserve package shape, proportions, crop, background colors, product photography, herbs, cheese photos, lighting and shadows.
- If the package contains words such as "FONTANA", "EST. 1978", "HALLOUMI ORIGINALET" or "FETA ORIGINALET", those words must remain exactly as in the reference image.
- Swedish grammar and spelling correction applies only to the new sales-sheet copy, never to text printed on product packages.

Mall: ${params.template.name}
Antal produkter: ${params.products.length}
Kedja/vecka: ${params.retailerLine || 'Ej angivet'}
Pratbubbla: ${params.speechBubble || ''}

Rubrik:
${params.copy.headline}

Ingress:
${params.copy.ingress}

USP-punkter:
${params.copy.usps.map((usp) => `- ${usp}`).join('\n')}

Produktdata:
${productLines}`;
}

function buildFsAdPrompt(params: {
  template: FsAdTemplate;
  products: ProductSlot[];
  adImage: DamSearchAssetResult | null;
  headline: string;
  ingress: string;
}): string {
  const productLines = params.products.map((slot, index) => {
    const product = buildCopyPayloadProduct(slot);
    return [
      `Produkt ${index + 1}:`,
      `Produktnamn: ${product.productName}`,
      `Artikelnummer: ${product.articleNumber}`,
      `Nettovikt: ${product.netContent}`,
      `Pris: ${product.price || 'Inget pris'}`,
    ].join('\n');
  }).join('\n\n');

  return `Skapa en färdig svensk Food Service-annons för Fontana som EN exakt renderad bild.

Använd referensbild 1 som annonsmall och grafisk profil. Följ format, blå profil, Fontana-logotyp, rubrikstil, prisetiketter, produktplacering och relationen mellan annonsbild och produktdel så nära som möjligt.
Referensbild 2 är annonsbilden. Placera den i annonsens bildyta och beskär den professionellt utan att den känns som en lös bild.
Referensbild 3 och framåt är produktbilder i samma ordning som produktlistan nedan.

VIKTIGT:
- All ny text ska vara exakt enligt rubrik, ingress och produktdata nedan, med korrekt svensk grammatik, stavning och typografi.
- Visa per produkt: produktnamn, artikelnummer och nettovikt. Visa pris endast om pris finns.
- Hitta inte på artikelnummer, nettovikt, pris eller produktnamn.
- Annonsen ska kännas som en professionell tryckannons för Food Service.
- Inga extra texter, inga vattenmärken, inga AI-kommentarer.

PRODUKTFÖRPACKNINGAR ÄR ORÖRBARA ASSETS:
- Product package references must be pixel-identical in appearance. Do NOT redesign, restyle, retranslate, correct, simplify, redraw, or "improve" them.
- Treat every product package as an untouchable asset: copy it into the layout, do not regenerate it.
- Preserve every visible brand element exactly, including wordmarks, subtexts, stamps, PDO seals, barcodes, weights, nutrition marks and Swedish body text.
- Preserve package shape, proportions, crop, background colors, product photography, lighting and shadows.
- Swedish grammar and spelling correction applies only to new ad copy, never to text printed on product packages.

Mall: ${params.template.name}
Antal produkter: ${params.products.length}
Annonsbild: ${params.adImage?.title || 'Vald annonsbild'}

Rubrik:
${params.headline}

Ingress:
${params.ingress}

Produktdata:
${productLines}`;
}

const SalesSheetPanel: React.FC<SalesSheetPanelProps> = ({
  config,
  categories,
  generating,
  onSearchDamAssets,
  onGenerateDraft,
}) => {
  const [activeTemplateTab, setActiveTemplateTab] = useState<'sales-sheet' | 'fs-ads'>('sales-sheet');
  const [templateId, setTemplateId] = useState(SALES_SHEET_TEMPLATES[0].id);
  const selectedTemplate = useMemo(
    () => SALES_SHEET_TEMPLATES.find((template) => template.id === templateId) || SALES_SHEET_TEMPLATES[0],
    [templateId]
  );
  const [productCount, setProductCount] = useState(SALES_SHEET_TEMPLATES[0].productCount);
  const [products, setProducts] = useState<ProductSlot[]>(() => makeSlots(SALES_SHEET_TEMPLATES[0].productCount));
  const [banner, setBanner] = useState<BannerState>({
    query: '',
    searching: false,
    results: [],
    selectedAsset: null,
  });
  const [imageSize, setImageSize] = useState('2K');
  const [quality, setQuality] = useState('high');
  const [outputFormat, setOutputFormat] = useState('jpeg');
  const [retailerLine, setRetailerLine] = useState('');
  const [speechBubble, setSpeechBubble] = useState('');
  const [copy, setCopy] = useState<SalesSheetCopyResult>({
    headline: '',
    ingress: '',
    usps: ['', '', ''],
  });
  const [fsTemplateId, setFsTemplateId] = useState(FS_AD_TEMPLATES[0].id);
  const selectedFsTemplate = useMemo(
    () => FS_AD_TEMPLATES.find((template) => template.id === fsTemplateId) || FS_AD_TEMPLATES[0],
    [fsTemplateId]
  );
  const [fsProductCount, setFsProductCount] = useState(2);
  const [fsProducts, setFsProducts] = useState<ProductSlot[]>(() => makeSlots(2));
  const [fsAdImage, setFsAdImage] = useState<BannerState>({
    query: '',
    searching: false,
    results: [],
    selectedAsset: null,
  });
  const [fsHeadline, setFsHeadline] = useState('');
  const [fsIngress, setFsIngress] = useState('');
  const [fsCopyLoading, setFsCopyLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const productSearchRunRef = useRef(0);
  const bannerSearchRunRef = useRef(0);
  const fsProductSearchRunRef = useRef(0);
  const fsAdImageSearchRunRef = useRef(0);

  const allProductsSelected = products.every((slot) => Boolean(slot.selectedAsset));
  const canGenerateCopy = allProductsSelected && !copyLoading;
  const canGenerateSheet = allProductsSelected && Boolean(banner.selectedAsset) && Boolean(copy.headline.trim()) && Boolean(copy.ingress.trim());
  const allFsProductsSelected = fsProducts.every((slot) => Boolean(slot.selectedAsset));
  const canGenerateFsCopy = allFsProductsSelected && !fsCopyLoading;
  const canGenerateFsAd = allFsProductsSelected && Boolean(fsAdImage.selectedAsset) && Boolean(fsHeadline.trim()) && Boolean(fsIngress.trim());
  const salesCostEstimateText = useMemo(() => {
    const model = getOpenAiModel(config);
    return estimateAiImageCostText(config, {
      provider: model.provider,
      model: model.id,
      prompt: buildSalesSheetPrompt({
        template: selectedTemplate,
        products,
        banner: banner.selectedAsset,
        speechBubble,
        retailerLine,
        copy,
      }),
      presetId: '',
      aspectRatio: '2:3',
      imageSize,
      quality,
      outputFormat,
      count: 1,
      references: estimateReferences(2 + products.length),
    });
  }, [banner.selectedAsset, config, copy, imageSize, outputFormat, products, quality, retailerLine, selectedTemplate, speechBubble]);
  const fsCostEstimateText = useMemo(() => {
    const model = getOpenAiModel(config);
    return estimateAiImageCostText(config, {
      provider: model.provider,
      model: model.id,
      prompt: buildFsAdPrompt({
        template: selectedFsTemplate,
        products: fsProducts,
        adImage: fsAdImage.selectedAsset,
        headline: fsHeadline,
        ingress: fsIngress,
      }),
      presetId: '',
      aspectRatio: selectedFsTemplate.aspectRatio,
      imageSize,
      quality,
      outputFormat,
      count: 1,
      references: estimateReferences(2 + fsProducts.length),
    });
  }, [config, fsAdImage.selectedAsset, fsHeadline, fsIngress, fsProducts, imageSize, outputFormat, quality, selectedFsTemplate]);
  const productCategoryIds = useMemo(() => getCategoryAndChildIds(categories, ['Produkter', 'Produkt']), [categories]);
  const bannerCategoryIds = useMemo(() => getCategoryAndChildIds(categories, ['Miljö', 'Recept']), [categories]);
  const productQueryKey = useMemo(() => products.map((slot) => slot.query.trim()).join('\u001f'), [products]);
  const fsProductQueryKey = useMemo(() => fsProducts.map((slot) => slot.query.trim()).join('\u001f'), [fsProducts]);

  const searchInCategories = useCallback(async (query: string, categoryIds: string[]) => {
    if (categoryIds.length === 0) return [];
    const resultSets = await Promise.all(categoryIds.map((categoryId) => onSearchDamAssets(query, categoryId)));
    return uniqueAssets(resultSets.flat());
  }, [onSearchDamAssets]);

  const handleTemplateChange = (nextTemplateId: string) => {
    const template = SALES_SHEET_TEMPLATES.find((item) => item.id === nextTemplateId) || SALES_SHEET_TEMPLATES[0];
    const nextProductCount = template.productCount;
    setTemplateId(template.id);
    setProductCount(nextProductCount);
    setProducts((prev) => makeSlots(nextProductCount, prev));
  };

  const handleProductCountChange = (nextProductCount: number) => {
    setProductCount(nextProductCount);
    setProducts((prev) => makeSlots(nextProductCount, prev));
  };

  const handleFsProductCountChange = (nextProductCount: number) => {
    setFsProductCount(nextProductCount);
    setFsProducts((prev) => makeSlots(nextProductCount, prev));
  };

  const updateProduct = useCallback((index: number, patch: Partial<ProductSlot>) => {
    setProducts((prev) => prev.map((slot, slotIndex) => (slotIndex === index ? { ...slot, ...patch } : slot)));
  }, []);

  const updateFsProduct = useCallback((index: number, patch: Partial<ProductSlot>) => {
    setFsProducts((prev) => prev.map((slot, slotIndex) => (slotIndex === index ? { ...slot, ...patch } : slot)));
  }, []);

  const searchProduct = async (index: number) => {
    const query = products[index]?.query.trim();
    if (query.length < 2) return;
    updateProduct(index, { searching: true });
    try {
      const results = await searchInCategories(query, productCategoryIds);
      updateProduct(index, { results, searching: false });
    } catch (error: any) {
      updateProduct(index, { searching: false });
      setLocalError(error?.message || 'Kunde inte söka produkt.');
    }
  };

  const searchBanner = async () => {
    const query = banner.query.trim();
    if (query.length < 2) return;
    setBanner((prev) => ({ ...prev, searching: true }));
    try {
      const results = await searchInCategories(query, bannerCategoryIds);
      setBanner((prev) => ({ ...prev, results, searching: false }));
    } catch (error: any) {
      setBanner((prev) => ({ ...prev, searching: false }));
      setLocalError(error?.message || 'Kunde inte söka bannerbild.');
    }
  };

  const searchFsProduct = async (index: number) => {
    const query = fsProducts[index]?.query.trim();
    if (query.length < 2) return;
    updateFsProduct(index, { searching: true });
    try {
      const results = await searchInCategories(query, productCategoryIds);
      updateFsProduct(index, { results, searching: false });
    } catch (error: any) {
      updateFsProduct(index, { searching: false });
      setLocalError(error?.message || 'Kunde inte söka produkt.');
    }
  };

  const searchFsAdImage = async () => {
    const query = fsAdImage.query.trim();
    if (query.length < 2) return;
    setFsAdImage((prev) => ({ ...prev, searching: true }));
    try {
      const results = await searchInCategories(query, bannerCategoryIds);
      setFsAdImage((prev) => ({ ...prev, results, searching: false }));
    } catch (error: any) {
      setFsAdImage((prev) => ({ ...prev, searching: false }));
      setLocalError(error?.message || 'Kunde inte söka annonsbild.');
    }
  };

  useEffect(() => {
    const currentRun = productSearchRunRef.current + 1;
    productSearchRunRef.current = currentRun;
    const timeout = window.setTimeout(() => {
      productQueryKey.split('\u001f').forEach((rawQuery, index) => {
        const query = rawQuery.trim();
        if (query.length < 2) {
          updateProduct(index, { results: [], searching: false });
          return;
        }
        updateProduct(index, { searching: true });
        searchInCategories(query, productCategoryIds)
          .then((results) => {
            if (productSearchRunRef.current === currentRun) {
              updateProduct(index, { results, searching: false });
            }
          })
          .catch((error: any) => {
            if (productSearchRunRef.current === currentRun) {
              updateProduct(index, { searching: false });
              setLocalError(error?.message || 'Kunde inte söka produkt.');
            }
          });
      });
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [productCategoryIds, productQueryKey, searchInCategories, updateProduct]);

  useEffect(() => {
    const query = banner.query.trim();
    const currentRun = bannerSearchRunRef.current + 1;
    bannerSearchRunRef.current = currentRun;
    if (query.length < 2) {
      setBanner((prev) => ({ ...prev, results: [], searching: false }));
      return;
    }
    const timeout = window.setTimeout(() => {
      setBanner((prev) => ({ ...prev, searching: true }));
      searchInCategories(query, bannerCategoryIds)
        .then((results) => {
          if (bannerSearchRunRef.current === currentRun) {
            setBanner((prev) => ({ ...prev, results, searching: false }));
          }
        })
        .catch((error: any) => {
          if (bannerSearchRunRef.current === currentRun) {
            setBanner((prev) => ({ ...prev, searching: false }));
            setLocalError(error?.message || 'Kunde inte söka bannerbild.');
          }
        });
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [banner.query, bannerCategoryIds, searchInCategories]);

  useEffect(() => {
    const currentRun = fsProductSearchRunRef.current + 1;
    fsProductSearchRunRef.current = currentRun;
    const timeout = window.setTimeout(() => {
      fsProductQueryKey.split('\u001f').forEach((rawQuery, index) => {
        const query = rawQuery.trim();
        if (query.length < 2) {
          updateFsProduct(index, { results: [], searching: false });
          return;
        }
        updateFsProduct(index, { searching: true });
        searchInCategories(query, productCategoryIds)
          .then((results) => {
            if (fsProductSearchRunRef.current === currentRun) {
              updateFsProduct(index, { results, searching: false });
            }
          })
          .catch((error: any) => {
            if (fsProductSearchRunRef.current === currentRun) {
              updateFsProduct(index, { searching: false });
              setLocalError(error?.message || 'Kunde inte söka produkt.');
            }
          });
      });
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [fsProductQueryKey, productCategoryIds, searchInCategories, updateFsProduct]);

  useEffect(() => {
    const query = fsAdImage.query.trim();
    const currentRun = fsAdImageSearchRunRef.current + 1;
    fsAdImageSearchRunRef.current = currentRun;
    if (query.length < 2) {
      setFsAdImage((prev) => ({ ...prev, results: [], searching: false }));
      return;
    }
    const timeout = window.setTimeout(() => {
      setFsAdImage((prev) => ({ ...prev, searching: true }));
      searchInCategories(query, bannerCategoryIds)
        .then((results) => {
          if (fsAdImageSearchRunRef.current === currentRun) {
            setFsAdImage((prev) => ({ ...prev, results, searching: false }));
          }
        })
        .catch((error: any) => {
          if (fsAdImageSearchRunRef.current === currentRun) {
            setFsAdImage((prev) => ({ ...prev, searching: false }));
            setLocalError(error?.message || 'Kunde inte söka annonsbild.');
          }
        });
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [fsAdImage.query, bannerCategoryIds, searchInCategories]);

  const handleGenerateCopy = async () => {
    setLocalError(null);
    setCopyLoading(true);
    try {
      const generated = await generateSalesSheetCopy({
        templateName: selectedTemplate.name,
        productCount,
        retailerLine,
        speechBubble,
        headlineHint: copy.headline,
        ingressHint: copy.ingress,
        uspHints: copy.usps.map((usp) => usp.trim()).filter(Boolean),
        products: products.map(buildCopyPayloadProduct),
      });
      setCopy({
        headline: generated.headline,
        ingress: generated.ingress,
        usps: generated.usps.length ? generated.usps : ['', '', ''],
      });
    } catch (error: any) {
      setLocalError(error?.response?.data?.message || error?.message || 'Kunde inte skapa säljbladscopy.');
    } finally {
      setCopyLoading(false);
    }
  };

  const handleGenerateFsCopy = async () => {
    setLocalError(null);
    setFsCopyLoading(true);
    try {
      const generated = await generateFsAdCopy({
        templateName: selectedFsTemplate.name,
        headlineHint: fsHeadline,
        ingressHint: fsIngress,
        products: fsProducts.map(buildCopyPayloadProduct),
      });
      setFsHeadline(generated.headline);
      setFsIngress(generated.ingress);
    } catch (error: any) {
      setLocalError(error?.response?.data?.message || error?.message || 'Kunde inte skapa FS-annonscopy.');
    } finally {
      setFsCopyLoading(false);
    }
  };

  const handleGenerateSheet = async () => {
    if (!canGenerateSheet) return;
    setLocalError(null);
    try {
      const templateDataUrl = await urlToDataUrl(selectedTemplate.assetUrl);
      const references: AiImageReferenceInput[] = [
        {
          referenceId: referenceId('sales-template'),
          type: 'upload',
          fileName: `${selectedTemplate.id}.png`,
          title: selectedTemplate.name,
          mimeType: 'image/png',
          dataUrl: templateDataUrl,
          url: selectedTemplate.assetUrl,
          thumbnailUrl: selectedTemplate.assetUrl,
        },
        {
          referenceId: referenceId('sales-banner'),
          type: 'dam_asset',
          assetId: banner.selectedAsset?._id,
          url: banner.selectedAsset?.previewUrl,
          thumbnailUrl: banner.selectedAsset?.thumbnailUrl,
          title: banner.selectedAsset?.title,
          fileName: banner.selectedAsset?.fileName,
          mimeType: banner.selectedAsset?.fileType,
        },
        ...products.map((slot) => ({
          referenceId: referenceId('sales-product'),
          type: 'dam_asset' as const,
          assetId: slot.selectedAsset?._id,
          url: slot.selectedAsset?.previewUrl,
          thumbnailUrl: slot.selectedAsset?.thumbnailUrl,
          title: slot.selectedAsset?.productName || slot.selectedAsset?.title,
          fileName: slot.selectedAsset?.fileName,
          mimeType: slot.selectedAsset?.fileType,
        })),
      ];
      const model = getOpenAiModel(config);
      onGenerateDraft({
        provider: model.provider,
        model: model.id,
        prompt: buildSalesSheetPrompt({
          template: selectedTemplate,
          products,
          banner: banner.selectedAsset,
          speechBubble,
          retailerLine,
          copy,
        }),
        presetId: '',
        workflow: 'sales_sheet',
        promptTemplateId: selectedTemplate.id,
        templatePrompt: selectedTemplate.name,
        manualPromptEdited: true,
        targetAssetId: undefined,
        aspectRatio: '2:3',
        imageSize,
        quality,
        outputFormat,
        count: 1,
        references,
      });
    } catch (error: any) {
      setLocalError(error?.message || 'Kunde inte starta säljbladsgenerering.');
    }
  };

  const handleGenerateFsAd = async () => {
    if (!canGenerateFsAd) return;
    setLocalError(null);
    try {
      const templateDataUrl = await urlToDataUrl(selectedFsTemplate.assetUrl);
      const references: AiImageReferenceInput[] = [
        {
          referenceId: referenceId('fs-template'),
          type: 'upload',
          fileName: `${selectedFsTemplate.id}.png`,
          title: selectedFsTemplate.name,
          mimeType: 'image/png',
          dataUrl: templateDataUrl,
          url: selectedFsTemplate.assetUrl,
          thumbnailUrl: selectedFsTemplate.assetUrl,
        },
        {
          referenceId: referenceId('fs-ad-image'),
          type: 'dam_asset',
          assetId: fsAdImage.selectedAsset?._id,
          url: fsAdImage.selectedAsset?.previewUrl,
          thumbnailUrl: fsAdImage.selectedAsset?.thumbnailUrl,
          title: fsAdImage.selectedAsset?.title,
          fileName: fsAdImage.selectedAsset?.fileName,
          mimeType: fsAdImage.selectedAsset?.fileType,
        },
        ...fsProducts.map((slot) => ({
          referenceId: referenceId('fs-product'),
          type: 'dam_asset' as const,
          assetId: slot.selectedAsset?._id,
          url: slot.selectedAsset?.previewUrl,
          thumbnailUrl: slot.selectedAsset?.thumbnailUrl,
          title: slot.selectedAsset?.productName || slot.selectedAsset?.title,
          fileName: slot.selectedAsset?.fileName,
          mimeType: slot.selectedAsset?.fileType,
        })),
      ];
      const model = getOpenAiModel(config);
      onGenerateDraft({
        provider: model.provider,
        model: model.id,
        prompt: buildFsAdPrompt({
          template: selectedFsTemplate,
          products: fsProducts,
          adImage: fsAdImage.selectedAsset,
          headline: fsHeadline,
          ingress: fsIngress,
        }),
        presetId: '',
        workflow: 'sales_sheet',
        promptTemplateId: selectedFsTemplate.id,
        templatePrompt: `FS annonser: ${selectedFsTemplate.name}`,
        manualPromptEdited: true,
        targetAssetId: undefined,
        aspectRatio: selectedFsTemplate.aspectRatio,
        imageSize,
        quality,
        outputFormat,
        count: 1,
        references,
      });
    } catch (error: any) {
      setLocalError(error?.message || 'Kunde inte starta FS-annonsgenerering.');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
          AI Mallar
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Skapa säljblad och Food Service-annonser med färdiga mallar, produktdata och samma AI Studio-flöde för godkännande.
        </Typography>
        <Tabs
          value={activeTemplateTab}
          onChange={(_, value) => setActiveTemplateTab(value)}
          sx={{ mt: 1.5, minHeight: 36 }}
        >
          <Tab value="sales-sheet" label="Säljblad" sx={{ minHeight: 36, textTransform: 'none' }} />
          <Tab value="fs-ads" label="FS annonser" sx={{ minHeight: 36, textTransform: 'none' }} />
        </Tabs>
      </Box>

      {localError && <Alert severity="error">{localError}</Alert>}

      {activeTemplateTab === 'sales-sheet' ? (
        <>
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
        <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
          <InputLabel id="sales-sheet-template-label">Mall</InputLabel>
          <Select
            labelId="sales-sheet-template-label"
            label="Mall"
            value={selectedTemplate.id}
            onChange={(event) => handleTemplateChange(String(event.target.value))}
          >
            {SALES_SHEET_TEMPLATES.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedTemplate.productCountOptions && (
          <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
            <InputLabel id="sales-sheet-product-count-label">Antal produkter</InputLabel>
            <Select
              labelId="sales-sheet-product-count-label"
              label="Antal produkter"
              value={productCount}
              onChange={(event) => handleProductCountChange(Number(event.target.value))}
            >
              {selectedTemplate.productCountOptions.map((count) => (
                <MenuItem key={count} value={count}>
                  {count}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {selectedTemplate.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {selectedTemplate.description}
          </Typography>
        )}

        <Box
          component="img"
          src={selectedTemplate.assetUrl}
          alt={selectedTemplate.name}
          sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'block' }}
        />
      </Box>

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
          GPT Image 2.5-inställningar
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="sales-sheet-size-label">Storlek</InputLabel>
              <Select
                labelId="sales-sheet-size-label"
                label="Storlek"
                value={imageSize}
                onChange={(event) => setImageSize(String(event.target.value))}
              >
                {(config?.imageSizes || ['1K', '2K', '4K']).map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FieldHelpIcon title={IMAGE_SIZE_HELP} label="Om storlek" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="sales-sheet-quality-label">Kvalitet</InputLabel>
              <Select
                labelId="sales-sheet-quality-label"
                label="Kvalitet"
                value={quality}
                onChange={(event) => setQuality(String(event.target.value))}
              >
                {(config?.openAiQualities || ['auto', 'low', 'medium', 'high', 'xhigh', 'max']).map((item) => (
                  <MenuItem key={item} value={item}>
                    {formatOpenAiQualityLabel(item)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FieldHelpIcon title={IMAGE_QUALITY_HELP} label="Om kvalitet" />
          </Box>
          <FormControl size="small" fullWidth>
            <InputLabel id="sales-sheet-format-label">Format</InputLabel>
            <Select
              labelId="sales-sheet-format-label"
              label="Format"
              value={outputFormat}
              onChange={(event) => setOutputFormat(String(event.target.value))}
            >
              {(config?.openAiOutputFormats || ['jpeg', 'png', 'webp']).map((format) => (
                <MenuItem key={format} value={format}>
                  {format.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Grundvalet är 2K, high och JPEG. Testa 1K eller medium om genereringen tar för lång tid.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {products.map((slot, index) => (
          <Accordion key={slot.id} defaultExpanded={index === 0} disableGutters sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Avatar variant="rounded" src={assetImageUrl(slot.selectedAsset)} sx={{ width: 36, height: 36 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Produkt {index + 1}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {slot.selectedAsset?.productName || slot.selectedAsset?.title || 'Ingen produkt vald'}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  label="Sök artikelnummer eller produktnamn"
                  value={slot.query}
                  onChange={(event) => updateProduct(index, { query: event.target.value })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void searchProduct(index);
                  }}
                  fullWidth
                />
                <Button variant="outlined" startIcon={slot.searching ? <CircularProgress size={16} /> : <ImageSearchIcon />} onClick={() => void searchProduct(index)}>
                  Sök
                </Button>
              </Box>

              {slot.results.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
                  {slot.results.slice(0, 5).map((asset) => (
                    <Button
                      key={asset._id}
                      variant={slot.selectedAsset?._id === asset._id ? 'contained' : 'outlined'}
                      onClick={() => updateProduct(index, { selectedAsset: asset })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', p: 1 }}
                    >
                      <Avatar variant="rounded" src={assetImageUrl(asset)} sx={{ width: 36, height: 36, mr: 1 }} />
                      <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {asset.productName || asset.title}
                        </Typography>
                        <Typography variant="caption" color="inherit">
                          Art: {asset.articleNumber || '-'} | EAN: {asset.ean || '-'} | Vikt: {asset.netContent || '-'}
                        </Typography>
                      </Box>
                    </Button>
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Pris"
                  value={slot.price}
                  disabled={slot.noPrice}
                  onChange={(event) => updateProduct(index, { price: event.target.value })}
                  placeholder="t.ex. 29:90"
                />
                <FormControlLabel
                  control={<Checkbox checked={slot.noPrice} onChange={(event) => updateProduct(index, { noPrice: event.target.checked })} />}
                  label="Inget pris"
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
          Bannerbild
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            size="small"
            label="Sök i DAM"
            value={banner.query}
            onChange={(event) => setBanner((prev) => ({ ...prev, query: event.target.value }))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void searchBanner();
            }}
            fullWidth
          />
          <Button variant="outlined" startIcon={banner.searching ? <CircularProgress size={16} /> : <ImageSearchIcon />} onClick={() => void searchBanner()}>
            Sök
          </Button>
        </Box>
        {banner.results.slice(0, 5).map((asset) => (
          <Button
            key={asset._id}
            variant={banner.selectedAsset?._id === asset._id ? 'contained' : 'outlined'}
            onClick={() => setBanner((prev) => ({ ...prev, selectedAsset: asset }))}
            sx={{ justifyContent: 'flex-start', textTransform: 'none', p: 1, width: '100%', mb: 0.75 }}
          >
            <Avatar variant="rounded" src={assetImageUrl(asset)} sx={{ width: 36, height: 36, mr: 1 }} />
            {asset.title}
          </Button>
        ))}
      </Box>

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
          Copy
        </Typography>
        <TextField
          size="small"
          label="Kedja/vecka eller intern instruktion"
          value={retailerLine}
          onChange={(event) => setRetailerLine(event.target.value)}
          fullWidth
          sx={{ mb: 1 }}
        />
        <TextField
          size="small"
          label="Text till pratbubbla"
          value={speechBubble}
          onChange={(event) => setSpeechBubble(event.target.value)}
          fullWidth
          sx={{ mb: 1 }}
        />
        <Button
          variant="contained"
          startIcon={copyLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
          disabled={!canGenerateCopy}
          onClick={() => void handleGenerateCopy()}
          sx={{ mb: 1.5, textTransform: 'none' }}
        >
          Generera copy med AI
        </Button>
        <TextField
          size="small"
          label="Rubrik"
          value={copy.headline}
          onChange={(event) => setCopy((prev) => ({ ...prev, headline: event.target.value }))}
          fullWidth
          sx={{ mb: 1 }}
        />
        <TextField
          size="small"
          label="Ingress"
          value={copy.ingress}
          onChange={(event) => setCopy((prev) => ({ ...prev, ingress: event.target.value }))}
          fullWidth
          multiline
          minRows={3}
          sx={{ mb: 1 }}
        />
        <TextField
          size="small"
          label="USPs, en per rad"
          value={copy.usps.join('\n')}
          onChange={(event) => setCopy((prev) => ({ ...prev, usps: event.target.value.split('\n').filter(Boolean).slice(0, 5) }))}
          fullWidth
          multiline
          minRows={4}
        />
      </Box>

      {salesCostEstimateText && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, px: 0.5 }}>
          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', flexShrink: 0 }}>
            Uppskattad kostnad
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, textAlign: 'right' }}>
            {salesCostEstimateText}
          </Typography>
        </Box>
      )}
      <Button
        size="large"
        variant="contained"
        disabled={!canGenerateSheet || generating}
        onClick={() => void handleGenerateSheet()}
        sx={{ borderRadius: 2.5, py: 1.2, textTransform: 'none', fontWeight: 800 }}
      >
        {generating ? 'Genererar...' : 'Generera säljblad'}
      </Button>
      {!canGenerateSheet && (
        <Typography variant="caption" color="text.secondary">
          Välj alla produkter, bannerbild och skapa eller fyll i copy innan generering.
        </Typography>
      )}
        </>
      ) : (
        <>
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
            <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
              <InputLabel id="fs-ad-template-label">Annonsmall</InputLabel>
              <Select
                labelId="fs-ad-template-label"
                label="Annonsmall"
                value={selectedFsTemplate.id}
                onChange={(event) => setFsTemplateId(String(event.target.value))}
              >
                {FS_AD_TEMPLATES.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
              <InputLabel id="fs-ad-product-count-label">Antal produkter</InputLabel>
              <Select
                labelId="fs-ad-product-count-label"
                label="Antal produkter"
                value={fsProductCount}
                onChange={(event) => handleFsProductCountChange(Number(event.target.value))}
              >
                {[1, 2, 3, 4].map((count) => (
                  <MenuItem key={count} value={count}>
                    {count}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {selectedFsTemplate.description}
            </Typography>
            <Box
              component="img"
              src={selectedFsTemplate.assetUrl}
              alt={selectedFsTemplate.name}
              sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'block' }}
            />
          </Box>

          <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              GPT Image 2.5-inställningar
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="fs-ad-size-label">Storlek</InputLabel>
                  <Select labelId="fs-ad-size-label" label="Storlek" value={imageSize} onChange={(event) => setImageSize(String(event.target.value))}>
                    {(config?.imageSizes || ['1K', '2K', '4K']).map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FieldHelpIcon title={IMAGE_SIZE_HELP} label="Om storlek" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="fs-ad-quality-label">Kvalitet</InputLabel>
                  <Select labelId="fs-ad-quality-label" label="Kvalitet" value={quality} onChange={(event) => setQuality(String(event.target.value))}>
                    {(config?.openAiQualities || ['auto', 'low', 'medium', 'high', 'xhigh', 'max']).map((item) => (
                      <MenuItem key={item} value={item}>
                        {formatOpenAiQualityLabel(item)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FieldHelpIcon title={IMAGE_QUALITY_HELP} label="Om kvalitet" />
              </Box>
              <FormControl size="small" fullWidth>
                <InputLabel id="fs-ad-format-label">Format</InputLabel>
                <Select labelId="fs-ad-format-label" label="Format" value={outputFormat} onChange={(event) => setOutputFormat(String(event.target.value))}>
                  {(config?.openAiOutputFormats || ['jpeg', 'png', 'webp']).map((format) => (
                    <MenuItem key={format} value={format}>
                      {format.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {fsProducts.map((slot, index) => (
              <Accordion key={slot.id} defaultExpanded={index === 0} disableGutters sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Avatar variant="rounded" src={assetImageUrl(slot.selectedAsset)} sx={{ width: 36, height: 36 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Produkt {index + 1}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {slot.selectedAsset?.productName || slot.selectedAsset?.title || 'Ingen produkt vald'}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      label="Sök artikelnummer eller produktnamn"
                      value={slot.query}
                      onChange={(event) => updateFsProduct(index, { query: event.target.value })}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') void searchFsProduct(index);
                      }}
                      fullWidth
                    />
                    <Button variant="outlined" startIcon={slot.searching ? <CircularProgress size={16} /> : <ImageSearchIcon />} onClick={() => void searchFsProduct(index)}>
                      Sök
                    </Button>
                  </Box>
                  {slot.results.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
                      {slot.results.slice(0, 5).map((asset) => (
                        <Button
                          key={asset._id}
                          variant={slot.selectedAsset?._id === asset._id ? 'contained' : 'outlined'}
                          onClick={() => updateFsProduct(index, { selectedAsset: asset })}
                          sx={{ justifyContent: 'flex-start', textTransform: 'none', p: 1 }}
                        >
                          <Avatar variant="rounded" src={assetImageUrl(asset)} sx={{ width: 36, height: 36, mr: 1 }} />
                          <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                              {asset.productName || asset.title}
                            </Typography>
                            <Typography variant="caption" color="inherit">
                              Art: {asset.articleNumber || '-'} | Vikt: {asset.netContent || '-'}
                            </Typography>
                          </Box>
                        </Button>
                      ))}
                    </Box>
                  )}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      label="Pris"
                      value={slot.price}
                      disabled={slot.noPrice}
                      onChange={(event) => updateFsProduct(index, { price: event.target.value })}
                      placeholder="t.ex. XX:00/kg"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={slot.noPrice} onChange={(event) => updateFsProduct(index, { noPrice: event.target.checked })} />}
                      label="Inget pris"
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Annonsbild
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                label="Sök i DAM"
                value={fsAdImage.query}
                onChange={(event) => setFsAdImage((prev) => ({ ...prev, query: event.target.value }))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void searchFsAdImage();
                }}
                fullWidth
              />
              <Button variant="outlined" startIcon={fsAdImage.searching ? <CircularProgress size={16} /> : <ImageSearchIcon />} onClick={() => void searchFsAdImage()}>
                Sök
              </Button>
            </Box>
            {fsAdImage.results.slice(0, 5).map((asset) => (
              <Button
                key={asset._id}
                variant={fsAdImage.selectedAsset?._id === asset._id ? 'contained' : 'outlined'}
                onClick={() => setFsAdImage((prev) => ({ ...prev, selectedAsset: asset }))}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', p: 1, width: '100%', mb: 0.75 }}
              >
                <Avatar variant="rounded" src={assetImageUrl(asset)} sx={{ width: 36, height: 36, mr: 1 }} />
                {asset.title}
              </Button>
            ))}
          </Box>

          <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Copy
            </Typography>
            <Button
              variant="contained"
              startIcon={fsCopyLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              disabled={!canGenerateFsCopy}
              onClick={() => void handleGenerateFsCopy()}
              sx={{ mb: 1.5, textTransform: 'none' }}
            >
              Generera copy med AI
            </Button>
            <TextField
              size="small"
              label="Rubrik"
              value={fsHeadline}
              onChange={(event) => setFsHeadline(event.target.value)}
              fullWidth
              sx={{ mb: 1 }}
            />
            <TextField
              size="small"
              label="Ingress"
              value={fsIngress}
              onChange={(event) => setFsIngress(event.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Box>

          {fsCostEstimateText && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, px: 0.5 }}>
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', flexShrink: 0 }}>
                Uppskattad kostnad
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, textAlign: 'right' }}>
                {fsCostEstimateText}
              </Typography>
            </Box>
          )}
          <Button
            size="large"
            variant="contained"
            disabled={!canGenerateFsAd || generating}
            onClick={() => void handleGenerateFsAd()}
            sx={{ borderRadius: 2.5, py: 1.2, textTransform: 'none', fontWeight: 800 }}
          >
            {generating ? 'Genererar...' : 'Generera FS-annons'}
          </Button>
          {!canGenerateFsAd && (
            <Typography variant="caption" color="text.secondary">
              Välj produkter, annonsbild och fyll i rubrik och ingress innan generering.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default SalesSheetPanel;
